package br.com.texeratx.boardgametimeapp.ui.screens.expansions.create

import br.com.texeratx.boardgametimeapp.core.i18n.LanguageManager
import br.com.texeratx.boardgametimeapp.core.image.PickedImage
import br.com.texeratx.boardgametimeapp.data.model.expansion.CreateExpansionRequest
import br.com.texeratx.boardgametimeapp.domain.usecases.expansions.CreateExpansionUseCase
import br.com.texeratx.boardgametimeapp.domain.usecases.games.GetGamesSimpleUseCase
import br.com.texeratx.boardgametimeapp.domain.usecases.upload.UploadExpansionImageUseCase
import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.launch

class CreateExpansionViewModel(
    private val getGamesSimpleUseCase: GetGamesSimpleUseCase,
    private val createExpansionUseCase: CreateExpansionUseCase,
    private val uploadExpansionImageUseCase: UploadExpansionImageUseCase,
    private val lang: LanguageManager
) : ScreenModel {

    val state = MutableStateFlow(CreateExpansionState())

    private var pendingImage: PickedImage? = null

    fun load(prefilledGameId: Long?) {
        screenModelScope.launch {
            state.value = state.value.copy(
                isLoadingGames = true,
                errorMessage = null,
                uiMessage = null
            )

            getGamesSimpleUseCase()
                .onSuccess { games ->
                    val selected = prefilledGameId?.let { id ->
                        games.firstOrNull { it.id == id }?.id ?: games.firstOrNull()?.id
                    }
                    state.value = state.value.copy(
                        games = games,
                        selectedGameId = selected,
                        isLoadingGames = false
                    )
                }
                .onFailure {
                    state.value = state.value.copy(
                        isLoadingGames = false,
                        errorMessage = it.message ?: lang.strings.value.errorLoadingGames
                    )
                }
        }
    }

    fun onNameChange(v: String) {
        state.value = state.value.copy(name = v)
    }

    fun onBggUrlChange(v: String) {
        state.value = state.value.copy(bggUrl = v)
    }

    fun onGameSelected(gameId: Long) {
        state.value = state.value.copy(selectedGameId = gameId)
    }

    fun onImageSelected(image: PickedImage) {
        pendingImage = image
        state.value = state.value.copy(
            selectedImageName = image.fileName,
            localImageBytes = image.bytes,
            localImageMimeType = image.mimeType
        )
    }

    fun clearLocalImage() {
        pendingImage = null
        state.value = state.value.copy(
            selectedImageName = null,
            localImageBytes = null,
            localImageMimeType = null
        )
    }

    fun createExpansion(onSuccess: () -> Unit) {
        val s = state.value
        val selectedGameId = s.selectedGameId

        if (s.name.isBlank()) {
            state.value = s.copy(errorMessage = lang.strings.value.expansionNameRequired)
            return
        }
        if (selectedGameId == null) {
            state.value = s.copy(errorMessage = lang.strings.value.selectAGame)
            return
        }

        screenModelScope.launch {
            state.value = s.copy(isSaving = true, errorMessage = null, uiMessage = null)

            // ✅ 1) upload (se tiver imagem local)
            var finalImageUrl: String? = s.imageUrl

            val pending = pendingImage
            if (pending != null) {
                state.value = state.value.copy(isUploadingImage = true)

                val uploadResult = uploadExpansionImageUseCase(
                    bytes = pending.bytes,
                    fileName = pending.fileName,
                    mimeType = pending.mimeType
                )

                uploadResult.fold(
                    onSuccess = { url ->
                        finalImageUrl = url
                        state.value = state.value.copy(
                            imageUrl = url,
                            isUploadingImage = false
                        )
                    },
                    onFailure = { e ->
                        state.value = state.value.copy(
                            isUploadingImage = false,
                            isSaving = false,
                            errorMessage = e.message ?: lang.strings.value.errorUploadingImage
                        )
                        return@launch
                    }
                )
            }

            // ✅ 2) cria expansion com a URL final
            val request = CreateExpansionRequest(
                name = state.value.name.trim(),
                gameId = selectedGameId,
                imageUrl = finalImageUrl,
                bggUrl = state.value.bggUrl.trim().takeIf { it.isNotBlank() },
                status = 1
            )

            createExpansionUseCase(request)
                .onSuccess {
                    state.value = state.value.copy(
                        isSaving = false,
                        uiMessage = lang.strings.value.expansionCreatedSuccess
                    )
                    onSuccess()
                }
                .onFailure {
                    state.value = state.value.copy(
                        isSaving = false,
                        errorMessage = it.message ?: lang.strings.value.errorCreatingExpansion
                    )
                }
        }
    }

    fun consumeMessage() {
        state.value = state.value.copy(uiMessage = null)
    }

    fun clearError() {
        state.value = state.value.copy(errorMessage = null)
    }
}
